import type { LeagueTooltipEntry } from "./league-tooltip";
import type { PageTextBinding } from "./pages";

export const pageTextVariables = [
  { key: "creator.name", label: "Creator name", group: "Creator" },
  { key: "creator.description", label: "Creator introduction", group: "Creator" },
  { key: "champion.name", label: "Main champion name", group: "League" },
  { key: "item.title", label: "Item title", group: "Collection item" },
  { key: "item.summary", label: "Item summary", group: "Collection item" },
  // Signed-in visitors only; empty for everyone else, so text around it should read well alone.
  { key: "member.name", label: "Member's first name", group: "Member" },
] as const;

export type PageTextVariable = (typeof pageTextVariables)[number]["key"];
export const pageImageVariables = [
  { key: "creator.heroImage", label: "Hero image" },
  { key: "creator.logo", label: "Profile image or logo" },
] as const;
/** The member Customize shows while previewing a signed-in visitor. */
export const sampleMemberName = "Sam";
export type PageTextValues = {
  /** `socials`: the website's social links, which a Social icons element draws (never text). */
  creator?: {
    name: string;
    description: string;
    coverUrl?: string;
    logoUrl?: string;
    socials?: readonly { label: string; url: string }[];
  } | null;
  /** The configured League champion; discovery data only, even when Matchups is off. */
  champion?: { name: string };
  item?: { title: string; summary: string };
  /** The signed-in visitor, when there is one; a page that uses it is private to them. */
  member?: { name: string } | null;
};

/** Preview examples supply a fallback; real sites supply their own public branding, even empty. */
export function resolvePageImage(
  image: { src: string; variable?: (typeof pageImageVariables)[number]["key"] },
  creator?: PageTextValues["creator"],
) {
  if (image.variable === "creator.heroImage") return creator?.coverUrl ?? image.src;
  if (image.variable === "creator.logo") return creator?.logoUrl ?? image.src;
  return image.src;
}

/** Variables expose discovery copy only. Values remain plain text, without evaluation or recursion. */
export function resolvePageText(text: string, values: PageTextValues) {
  const fields: Record<PageTextVariable, string> = {
    "creator.name": values.creator?.name ?? "",
    "creator.description": values.creator?.description ?? "",
    "champion.name": values.champion?.name ?? "",
    "item.title": values.item?.title ?? "",
    "item.summary": values.item?.summary ?? "",
    "member.name": values.member?.name ?? "",
  };
  return text.replace(/\{\{\s*([a-z]+\.[a-z]+)\s*\}\}/g, (token, key: string) =>
    Object.hasOwn(fields, key) ? fields[key as PageTextVariable] : token,
  );
}

/** The same champion on every page, taken from the site's selected configuration. */
export function pageChampion(
  experiences: readonly { kind: string; mainChampion?: string }[],
): PageTextValues["champion"] {
  const name = experiences.find((experience) => experience.kind === "matchups")?.mainChampion;
  return name ? { name } : undefined;
}

export function usesChampionVariable(text: string) {
  return /\{\{\s*champion\.name\s*\}\}/.test(text);
}

/** Keep authored tooltip overrides attached when their game reference contains a variable. */
export function resolvePageTooltipEntries(
  entries: readonly LeagueTooltipEntry[] | undefined,
  values: PageTextValues,
) {
  return entries?.map((entry) => ({ ...entry, token: resolvePageText(entry.token, values) }));
}

export function insertPageTextVariable(
  text: string,
  key: PageTextVariable,
  start: number,
  end = start,
) {
  const before = Math.max(0, Math.min(text.length, start));
  const after = Math.max(before, Math.min(text.length, end));
  const token = `{{${key}}}`;
  return { text: text.slice(0, before) + token + text.slice(after), caret: before + token.length };
}

export type PageItemFields = {
  title: string;
  summary: string;
  date?: string | null;
  price?: string | null;
  /** A matchup guide's difficulty key ("hard") and verdict, while the visitor may read them. */
  difficulty?: string | null;
  verdict?: string | null;
};

/**
 * A bound card field as plain text. Dates read "Aug 12, 2025" in UTC, so server and editor agree;
 * a difficulty reads "Hard".
 */
export function pageItemField(item: PageItemFields | undefined, field: PageTextBinding) {
  if (!item) return "";
  if (field === "difficulty")
    return item.difficulty ? item.difficulty[0]!.toUpperCase() + item.difficulty.slice(1) : "";
  if (field !== "date") return item[field] ?? "";
  const date = item.date ? new Date(item.date) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : "";
}
